import { getYear, getMonth, startOfMonth, endOfMonth, format, parseISO, } from 'date-fns';
import { isNotLoadedState, } from 'reducers/types';
import { createSelector } from 'reselect';
import getMonthlyTotals from 'utils/api/getMonthlytotals';
import { createNonMemoizedSelector } from 'store/selectors';
import { selectHasSetPaydayRange } from 'reducers/selectors';
import { getAnalyticsCategories } from './utils/getAnalyticsCategories';
import { emptyArray } from '../../constants';
export const selectIsFetchingTotals = (store) => store.expenses.isFetchingTotals;
export const selectAnalyticsPosition = (store) => store.expenses.analyticsPosition ?? 0;
export const selectAnalyticsFirstItem = (store) => store.expenses.analyticsFirstItem ?? 0;
export const selectBudgetingPosition = (store) => store.expenses.budgetingPosition;
export const selectExpensesScreen = (store) => store.utils.currentScreen;
// In analytics 'monthly' does not really necessarily mean monthly, it could mean, monthly, yearly, their pay period, weekly, or a custom period
// It simply means each card in the analytics carousel
export const selectMonthlyCategories = (store) => store.expenses.monthlyCategories;
export const selectMonthlyMerchants = (store) => store.expenses.monthlyMerchants;
export const selectMonthlyCategoriesForPeriod = (store, from) => from != null ? store.expenses.monthlyCategories[from] : undefined;
export const selectMonthlyMerchantsForPeriod = (store, from) => from != null ? store.expenses.monthlyMerchants[from] : undefined;
export const selectIsSwitchingTotalsStep = (store) => store.expenses.isSwitchingTotalsStep;
export const flattenMonthlyTotals = (monthlyTotals) => {
    const initialValue = {
        value: 0,
        income: 0,
        spending: 0,
        to: monthlyTotals[0].from,
        from: monthlyTotals[monthlyTotals.length - 1].from,
        currency: monthlyTotals[0].currency,
        hasCircle: false,
        isPayday: false,
        totalBudget: null,
        rollingAccumulatedLimit: null,
        spendBreakdown: flattenSpendingBreakdowns(monthlyTotals),
    };
    return [
        monthlyTotals.reduce((prev, curr) => ({
            ...prev,
            value: curr.value + prev.value,
            income: curr.income + prev.income,
            spending: curr.spending + prev.spending,
        }), initialValue),
    ];
};
export const selectMonthlyTotalsFiltered = createSelector([(store) => store.expenses.monthlyTotalsFiltered], (filtered) => {
    if (!filtered) {
        return undefined;
    }
    const results = getMonthlyTotals(filtered.results);
    return {
        ...filtered,
        results,
    };
});
const generateFakeMonths = (oldestMonthlyTotal) => {
    const currentYear = getYear(new Date());
    const lastRealResult = parseISO(oldestMonthlyTotal.from);
    const lastRealYear = getYear(lastRealResult);
    const lastRealMonth = getMonth(lastRealResult);
    const fakeResults = [];
    for (let year = lastRealYear; year >= 2010; year--) {
        for (let month = year === currentYear ? getMonth(lastRealResult) : 11; month >= 0; month--) {
            const from = startOfMonth(new Date(year, month - 1));
            const endOfMonthDate = endOfMonth(from);
            if (year !== lastRealYear || month <= lastRealMonth) {
                fakeResults.push({
                    from: format(from, 'yyyy-MM-dd'),
                    to: format(endOfMonthDate, 'yyyy-MM-dd'),
                    loaded: false,
                    income: 0,
                    currency: oldestMonthlyTotal.currency,
                    hasCircle: false,
                    isPayday: false,
                    spending: 0,
                    totalBudget: null,
                    value: 0,
                    rollingAccumulatedLimit: null,
                });
            }
        }
    }
    return fakeResults;
};
export const selectMonthlyTotalsFilteredWithFallbackMonths = createSelector([selectMonthlyTotalsFiltered], (filtered) => {
    if (!filtered)
        return undefined;
    if (filtered.step === 'month') {
        const { results } = filtered;
        // There is an issue with the carousel that means it cant scroll back when changing data and setting another first index
        // I don’t 100% understand why, but populating the list with fake months is a workaround
        // we can then call fetchMoreAnalyticsBudgetingData when an item with loaded: false is reached
        const fakeResults = generateFakeMonths(results[results.length - 1]);
        return {
            ...filtered,
            results: [...results, ...fakeResults],
        };
    }
    return filtered;
}, {
    devModeChecks: {
        identityFunctionCheck: 'never',
    },
});
export const selectMonthlyTotals = createSelector([(store) => store.expenses.monthlyTotals], (monthlyTotals) => getMonthlyTotals(monthlyTotals));
export const selectCurrentMonthlyTotals = createNonMemoizedSelector([selectMonthlyTotalsFilteredWithFallbackMonths, selectMonthlyTotals], (filteredMonthlyTotals, unfilteredMonthlyTotals) => filteredMonthlyTotals?.results || unfilteredMonthlyTotals);
export const selectIsCustomFilterEnabled = createSelector([selectMonthlyTotalsFiltered], (monthlyTotalsFiltered) => monthlyTotalsFiltered !== undefined);
const frmtPeriodKey = (total) => total ? `${total.from}|${total.to}` : undefined;
export const selectAnalyticsMerchantCurrentLoadingState = createSelector([
    selectAnalyticsPosition,
    selectCurrentMonthlyTotals,
    selectMonthlyMerchants,
    selectIsSwitchingTotalsStep,
], (position, monthlyTotals, monthlyMerchants, isSwitchingTotalsStep) => {
    const periodKey = frmtPeriodKey(monthlyTotals[position]);
    const data = periodKey ? monthlyMerchants[periodKey] : undefined;
    return (isNotLoadedState(data?.loadingState) ||
        monthlyTotals.length === 0 ||
        isSwitchingTotalsStep);
});
export const selectAnalyticsCategoryCurrentLoadingState = createSelector([
    selectAnalyticsPosition,
    selectCurrentMonthlyTotals,
    selectMonthlyCategories,
    selectIsSwitchingTotalsStep,
], (position, monthlyTotals, monthlyCategories, isSwitchingTotalsStep) => {
    const periodKey = frmtPeriodKey(monthlyTotals[position]);
    const data = periodKey ? monthlyCategories[periodKey] : undefined;
    return (isNotLoadedState(data?.loadingState) ||
        monthlyTotals.length === 0 ||
        isSwitchingTotalsStep);
});
export const selectMonthlyCategoryFromCategoryId = createSelector([
    selectAnalyticsPosition,
    selectCurrentMonthlyTotals,
    selectMonthlyCategories,
    (_, categoryId) => categoryId,
], (position, monthlyTotals, monthlyCategories, categoryId) => {
    if (!categoryId)
        return undefined;
    const periodKey = frmtPeriodKey(monthlyTotals[position]);
    const data = periodKey ? monthlyCategories[periodKey] : undefined;
    return data?.categories?.find((category) => category.id === categoryId);
});
const toDay = (date) => format(parseISO(date), 'yyyy-MM-dd');
/**
 * Finds a category in the loaded period that matches `from`/`to` (yyyy-MM-dd),
 * regardless of which screen's carousel position that period belongs to.
 */
export const selectMonthlyCategoryForPeriod = (store, categoryId, from, to) => {
    if (!categoryId || !from || !to)
        return undefined;
    return Object.keys(store.expenses.monthlyCategories)
        .filter((key) => {
        const [keyFrom, keyTo] = key.split('|');
        return toDay(keyFrom) === from && toDay(keyTo) === to;
    })
        .map((key) => store.expenses.monthlyCategories[key]?.categories.find((category) => category.id === categoryId))
        .find(Boolean);
};
export const selectMonthlyMerchantFromMerchantId = createSelector([
    selectAnalyticsPosition,
    selectCurrentMonthlyTotals,
    selectMonthlyMerchants,
    (_, merchantId) => merchantId,
], (position, monthlyTotals, monthlyMerchants, merchantId) => {
    if (typeof merchantId !== 'number')
        return undefined;
    const periodKey = frmtPeriodKey(monthlyTotals[position]);
    const data = periodKey ? monthlyMerchants[periodKey] : undefined;
    return data?.merchants.find((merchant) => merchant.id === merchantId);
});
export const selectMonthlyCategoryOrMerchantFromId = createSelector([
    (store, categoryId) => selectMonthlyCategoryFromCategoryId(store, categoryId),
    (store, _categoryId, merchantId) => selectMonthlyMerchantFromMerchantId(store, merchantId),
], (category, merchant) => {
    if (category)
        return { isMerchant: false, monthly: category };
    if (merchant)
        return { isMerchant: true, monthly: merchant };
    return null;
});
export const selectAnalyticsPeriodData = createSelector([
    selectAnalyticsPosition,
    selectCurrentMonthlyTotals,
    selectMonthlyCategories,
    (_, customPosition) => customPosition,
], (position, monthlyTotals, monthlyCategories, customPosition) => {
    const periodKey = frmtPeriodKey(monthlyTotals[customPosition !== undefined ? customPosition : position]);
    return periodKey ? monthlyCategories[periodKey] : undefined;
});
export const selectAnalyticsCategoriesMap = createSelector([
    (store, customPosition) => selectAnalyticsPeriodData(store, customPosition),
], (data) => {
    if (!data)
        return undefined;
    const sorted = [...data.categories].sort((a, b) => a.total > b.total ? 1 : -1);
    const categoriesMap = sorted.reduce((acc, curr) => {
        if (curr.parentCategoryId) {
            acc.subCategories[curr.id] = curr;
        }
        else {
            acc.parentCategories[curr.id] = curr;
        }
        return acc;
    }, {
        parentCategories: {},
        subCategories: {},
    });
    return categoriesMap;
});
export const selectAnalyticsCategorySections = createSelector([
    selectAnalyticsPosition,
    selectCurrentMonthlyTotals,
    selectAnalyticsPeriodData,
    selectAnalyticsCategoriesMap,
], (position, monthlyTotals, data, categoriesMap) => {
    if (!categoriesMap ||
        !data?.categories ||
        (position === 0 && !data?.transactionsCount) ||
        (data &&
            monthlyTotals[position] &&
            !data.transactionsCount &&
            position !== 0)) {
        return emptyArray;
    }
    const categories = getAnalyticsCategories(Object.values(categoriesMap.parentCategories));
    const currency = monthlyTotals && monthlyTotals[position]
        ? monthlyTotals[position].currency
        : '';
    const totalSpending = monthlyTotals && monthlyTotals[position]
        ? monthlyTotals[position].spending
        : 0;
    const expenses = categories[0].data;
    const income = categories.length > 1 ? categories[1].data : [];
    const incomeTotal = categories.length > 1 && categories[1] ? categories[1].total : 0;
    const sections = [{ type: 'marker' }];
    const visibleExpensesWithTransactions = expenses.filter((e) => e.transactionsCount > 0 && !e.hideFromStats);
    if (visibleExpensesWithTransactions.length > 0) {
        sections.push({
            type: 'header',
            data: {
                id: 'spending',
                title: 'Spending',
                amount: -totalSpending,
                currency,
            },
        });
        sections.push(...visibleExpensesWithTransactions.map((data, index) => ({
            type: 'category',
            data,
            totalSpending,
            index,
            isLast: index === visibleExpensesWithTransactions.length - 1,
        })));
    }
    if (income.length > 0) {
        sections.push({
            type: 'header',
            data: { id: 'income', title: 'Income', amount: incomeTotal, currency },
        });
        sections.push(...income.map((data, index) => ({
            type: 'category',
            totalSpending: incomeTotal,
            data,
            index,
            isLast: index === income.length - 1,
        })));
    }
    const hiddenExpensesWithTransactions = expenses.filter((e) => e.transactionsCount > 0 && e.hideFromStats);
    if (hiddenExpensesWithTransactions.length > 0) {
        sections.push({
            type: 'header',
            data: {
                id: 'excluded',
                title: 'Excluded categories',
                amount: 0,
                currency,
            },
        });
    }
    sections.push(...hiddenExpensesWithTransactions.map((data, index) => ({
        type: 'category',
        data,
        index,
        isLast: index === hiddenExpensesWithTransactions.length - 1,
    })));
    return sections;
});
export const selectAnalyticsMerchantSections = createSelector([selectAnalyticsPosition, selectCurrentMonthlyTotals, selectMonthlyMerchants], (position, monthlyTotals, monthlyMerchants) => {
    const periodKey = frmtPeriodKey(monthlyTotals[position]);
    const data = periodKey ? monthlyMerchants[periodKey] : undefined;
    const filtered = data?.merchants.filter((merchant) => merchant.transactionsCount > 0) ||
        [];
    const sections = filtered.map((data, index) => ({
        type: 'merchant',
        data,
        index,
        isLast: index === filtered.length - 1,
    }));
    return sections;
});
export const selectCategoryTotals = (store, id) => store.expenses.categoryTotals[id ?? ''];
export const selectCategoryExpenses = (store, id) => store.expenses.categoryExpenses[id ?? ''];
export const selectHasPendingCategoryEdits = (store) => store.expenses.pendingCategoryEdits > 0;
export const selectChartType = (store) => store.analytics.chartTypeV2;
export const selectShowOverTimeRunningSum = (store) => store.analytics.showOverTimeRunningSum;
export const selectNeedsAnalyticsIntro = createSelector([
    selectHasSetPaydayRange,
    (store) => store.deviceSettings.didSetPeriod,
    (store) => store.feed.feed.cards.thisMonth,
], (hasPaydayRange, didSetPeriod, thisMonthCard) => !hasPaydayRange && !didSetPeriod && thisMonthCard?.budget == null);
const flattenSpendingBreakdowns = (breakdowns) => {
    const flattened = breakdowns
        .slice()
        .reverse()
        .reduce((prev, curr, index) => ({
        ...prev,
        spendBreakdown: [
            ...(curr.spendBreakdown
                ? curr.spendBreakdown.map((value) => ({
                    ...value,
                    cumulative: index > 0 && prev.spendBreakdown[0]
                        ? prev.spendBreakdown[0].cumulative + value.cumulative
                        : value.cumulative,
                }))
                : []),
            ...prev.spendBreakdown,
        ],
    }), { spendBreakdown: [] });
    return flattened.spendBreakdown.reverse();
};
